import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './EmailApproval.css';
import { api } from '../config';

interface InitiativeDetail {
  id: number;
  use_case_name: string;
  proposed_solution_owner: string;
  solution_sponsor: string;
  submitted_by: string;
  submitted_date: string;
  purpose: string;
  value: string;
  users: string;
  solution_scope: string;
  proposed_workflow: string;
  inputs_and_data_sources: string;
  status: string;
}

const EmailApproval: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const [initiativeId, setInitiativeId] = useState<number | null>(null);
  const [initiative, setInitiative] = useState<InitiativeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  // Parse token to get initiative ID
  useEffect(() => {
    if (!token) {
      setError('No approval token provided. Please use the link from the email.');
      setLoading(false);
      return;
    }

    try {
      // Decode JWT manually to get initiative_id (without verification on client)
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        throw new Error('Invalid token format');
      }
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      
      if (!payload.initiative_id) {
        throw new Error('Invalid token: missing initiative_id');
      }
      
      setInitiativeId(payload.initiative_id);
      
      // Fetch initiative details
      const fetchInitiative = async () => {
        try {
          const response = await fetch(
            api(`/api/initiatives/${payload.initiative_id}/view-from-email?token=${encodeURIComponent(token)}`),
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to fetch initiative details');
          }
          
          const data = await response.json();
          setInitiative(data);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load initiative details'
          );
        } finally {
          setLoading(false);
        }
      };
      
      fetchInitiative();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Invalid or expired approval link'
      );
      setLoading(false);
    }
  }, [token]);

  const handleApprove = async () => {
    if (!initiativeId || !token) return;

    setActionInProgress(true);
    try {
      const response = await fetch(
        api(`/api/initiatives/${initiativeId}/approve-from-email?token=${encodeURIComponent(token)}&comment=${encodeURIComponent(approvalComment)}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || 'Failed to approve initiative'
        );
      }

      setSuccessMessage(
        `✓ Initiative "${initiative?.use_case_name}" has been successfully approved!`
      );
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error approving initiative'
      );
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReject = async () => {
    if (!initiativeId || !token) return;

    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActionInProgress(true);
    try {
      const response = await fetch(
        api(`/api/initiatives/${initiativeId}/reject-from-email?token=${encodeURIComponent(token)}&comment=${encodeURIComponent(rejectionReason)}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || 'Failed to reject initiative'
        );
      }

      setSuccessMessage(
        `✓ Initiative "${initiative?.use_case_name}" has been successfully rejected.`
      );
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error rejecting initiative'
      );
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="email-approval-container">
        <div className="loading-spinner">Loading initiative details...</div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="email-approval-container">
        <div className="error-message">
          <h2>❌ Invalid Link</h2>
          <p>No approval token provided. Please use the link from the email.</p>
        </div>
      </div>
    );
  }

  if (error && !successMessage) {
    return (
      <div className="email-approval-container">
        <div className="error-message">
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Go to Home</button>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="email-approval-container">
        <div className="success-message">
          <h2>{successMessage}</h2>
          <p>Redirecting you back to the application...</p>
        </div>
      </div>
    );
  }

  if (!initiative) {
    return (
      <div className="email-approval-container">
        <div className="error-message">
          <h2>❌ Initiative Not Found</h2>
          <p>The initiative could not be found.</p>
        </div>
      </div>
    );
  }

  if (initiative.status !== 'pending') {
    return (
      <div className="email-approval-container">
        <div className="error-message">
          <h2>⚠️ Initiative Already Processed</h2>
          <p>
            This initiative is no longer pending and cannot be
            {' '}
            {initiative.status === 'approved' ? 'approved' : 'rejected'}
            .
          </p>
          <p>Current status: <strong>{initiative.status}</strong></p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-approval-container">
      <div className="approval-card">
        <div className="approval-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '10px' }}>
            <img src="/logo.png" alt="ABB" style={{ height: '40px', width: 'auto' }} />
            <h1>MODP AI Solution Prescreener</h1>
          </div>
        </div>

        <div className="initiative-summary">
          <h2>{initiative.use_case_name}</h2>
          
          <div className="summary-grid">
            <div className="summary-item">
              <label>Request ID</label>
              <span>#{initiative.id}</span>
            </div>
            <div className="summary-item">
              <label>Submitted By</label>
              <span>{initiative.submitted_by}</span>
            </div>
            <div className="summary-item">
              <label>Submission Date</label>
              <span>{new Date(initiative.submitted_date).toLocaleDateString()}</span>
            </div>
            <div className="summary-item">
              <label>Status</label>
              <span className="badge-pending">{initiative.status}</span>
            </div>
          </div>

          <div style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '30px' }}>
            <h3 style={{ color: '#ee0000', marginBottom: '20px', fontSize: '16px' }}>Initiative Details</h3>
            <div className="summary-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {initiative.proposed_solution_owner && (
                <div className="summary-item">
                  <label>Proposed Solution Owner</label>
                  <span>{initiative.proposed_solution_owner}</span>
                </div>
              )}
              {initiative.solution_sponsor && (
                <div className="summary-item">
                  <label>Solution Sponsor</label>
                  <span>{initiative.solution_sponsor}</span>
                </div>
              )}
              {initiative.purpose && (
                <div className="summary-item">
                  <label>Purpose</label>
                  <span>{initiative.purpose}</span>
                </div>
              )}
              {initiative.value && (
                <div className="summary-item">
                  <label>Value</label>
                  <span>{initiative.value}</span>
                </div>
              )}
              {initiative.users && (
                <div className="summary-item">
                  <label>Intended Users</label>
                  <span>{initiative.users}</span>
                </div>
              )}
              {initiative.solution_scope && (
                <div className="summary-item">
                  <label>Solution Scope</label>
                  <span>{initiative.solution_scope}</span>
                </div>
              )}
              {initiative.proposed_workflow && (
                <div className="summary-item">
                  <label>Proposed Workflow</label>
                  <span>{initiative.proposed_workflow}</span>
                </div>
              )}
              {initiative.inputs_and_data_sources && (
                <div className="summary-item">
                  <label>Inputs & Data Sources</label>
                  <span>{initiative.inputs_and_data_sources}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!showRejectionForm ? (
          <div className="action-buttons">
            <button
              className="btn-approve"
              onClick={handleApprove}
              disabled={actionInProgress}
            >
              {actionInProgress ? 'Processing...' : '✓ Approve Initiative'}
            </button>
            <button
              className="btn-reject"
              onClick={() => setShowRejectionForm(true)}
              disabled={actionInProgress}
            >
              ✗ Reject Initiative
            </button>
          </div>
        ) : (
          <div className="rejection-form">
            <div className="form-group">
              <label htmlFor="rejection-reason">
                Please provide a reason for rejection:
              </label>
              <textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter your reason for rejection..."
                rows={5}
              />
            </div>

            <div className="form-buttons">
              <button
                className="btn-reject-confirm"
                onClick={handleReject}
                disabled={actionInProgress || !rejectionReason.trim()}
              >
                {actionInProgress ? 'Processing...' : 'Confirm Rejection'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowRejectionForm(false);
                  setRejectionReason('');
                }}
                disabled={actionInProgress}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showRejectionForm && (
          <div className="approval-comment-section">
            <label htmlFor="approval-comment">
              💡 Optional: Add a comment with your approval:
            </label>
            <textarea
              id="approval-comment"
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder="Your comment here (optional)..."
              rows={4}
            />
          </div>
        )}

        <div className="footer-note">
          <p>
            ℹ️ You can also review this initiative in the application by
            logging in and navigating to the screening dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailApproval;
